import React, { useState, useEffect } from 'react';
import { submitFormData } from '../../../../pages/api';

const Curing = ({ formData = {}, onFormDataChange }) => {
    // Destructure formData to get initial values
    const {
        environmentalConditionsObservations = [],
        curingObservations = [],
        nonConformance = '',
    } = formData;

    // State to hold table data (product, specifications, frequency)
    const [environmentalConditionsData, setEnvironmentalConditionsData] = useState([]);
    const [curingData, setCuringData] = useState([]);

    // Fetch metadata and inspectionData only once on component mount
    useEffect(() => {
        const metadata = JSON.parse(sessionStorage.getItem('metadata'));
        const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData'));

        // Initialize table data from metadata
        if (metadata && metadata.post && metadata.post.Curing_post) {
            const { EnvironmentalConditions, Curing } = metadata.post.Curing_post;
            setEnvironmentalConditionsData(EnvironmentalConditions || []);
            setCuringData(Curing || []);
        }

        // Initialize observations and non-conformance from inspectionData only if formData is empty
        if (
            inspectionData &&
            inspectionData.PostLamination &&
            inspectionData.PostLamination.Curing_post &&
            (!environmentalConditionsObservations.length &&
                !curingObservations.length &&
                !nonConformance.trim())
        ) {
            const { EnvironmentalConditions, Curing, nonConformance: inspectionNonConformance } =
                inspectionData.PostLamination.Curing_post;

            // Map observation data from inspectionData
            const prefilledEnvironmentalConditionsObservations = EnvironmentalConditions?.map((item) => ({
                observation1: item.observation1 || '',
                observation2: item.observation2 || '',
            })) || [];

            const prefilledCuringObservations = Curing?.map((item) => ({
                observation1: item.observation1 || '',
                observation2: item.observation2 || '',
            })) || [];

            // Update formData with observations and non-conformance
            onFormDataChange({
                ...formData,
                environmentalConditionsObservations: prefilledEnvironmentalConditionsObservations,
                curingObservations: prefilledCuringObservations,
                nonConformance: inspectionNonConformance || '',
            });
        }
    }, []); // Empty dependency array ensures this runs only once on mount

    const handleObservationChange = (index, field, value, isEnvironmental) => {
        const fieldKey = isEnvironmental ? 'environmentalConditionsObservations' : 'curingObservations';
        const updatedObservations = [...(formData[fieldKey] || [])];
        updatedObservations[index] = {
            ...updatedObservations[index],
            [field]: value,
        };
        onFormDataChange({ ...formData, [fieldKey]: updatedObservations });
    };

    const handleNonConformanceChange = (e) => {
        onFormDataChange({ ...formData, nonConformance: e.target.value });
    };

    const submitObservations = async () => {
        const projectId = localStorage.getItem('selectedProject');
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectorName = sessionStorage.getItem('username');
        const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

        // Combine environmental conditions data with observations
        const combinedEnvironmentalConditionsData = environmentalConditionsData.map((item, index) => ({
            product: item.product || '',
            specifications: item.specifications || '',
            frequency: item.frequency || '',
            observation1: formData.environmentalConditionsObservations?.[index]?.observation1 || '',
            observation2: formData.environmentalConditionsObservations?.[index]?.observation2 || '',
        }));

        // Combine curing data with observations
        const combinedCuringData = curingData.map((item, index) => ({
            product: item.product || '',
            specifications: item.specifications || '',
            frequency: item.frequency || '',
            observation1: formData.curingObservations?.[index]?.observation1 || '',
            observation2: formData.curingObservations?.[index]?.observation2 || '',
        }));

        const payload = {
            projectId,
            inspectionSupplier,
            inspectionDate,
            inspectionShift,
            inspectorName,
            status: 'completed',
            PostLamination: {
                Curing_post: {
                    EnvironmentalConditions: combinedEnvironmentalConditionsData,
                    Curing: combinedCuringData,
                    nonConformance: formData.nonConformance || '',
                },
            },
        };

        try {
            await submitFormData(payload);
            alert('Observations submitted successfully');
        } catch (error) {
            console.error('Error submitting observations:', error);
            alert('Failed to submit observations');
        }
    };

    const renderTable = (title, data, fieldKey, isEnvironmental) => (
        <div className="mt-6">
            <h4 className="text-lg font-semibold mb-4 text-gray-700">{title}</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b last:border-none ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 transition-colors duration-200`}
                            >
                                <td className="py-3 px-4">{item.product || ''}</td>
                                <td className="py-3 px-4">{item.specifications || ''}</td>
                                <td className="py-3 px-4">{item.frequency || ''}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={formData[fieldKey]?.[index]?.observation1 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation1', e.target.value, isEnvironmental)}
                                        placeholder="Enter observation 1"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={formData[fieldKey]?.[index]?.observation2 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation2', e.target.value, isEnvironmental)}
                                        placeholder="Enter observation 2"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Curing Inspection</h3>

            {renderTable("Environmental Conditions", environmentalConditionsData, 'environmentalConditionsObservations', true)}
            {renderTable("Curing", curingData, 'curingObservations', false)}

            <div className="mt-6">
                <h4 className="text-lg font-semibold mb-2 text-gray-700">Non Conformance</h4>
                <textarea
                    value={formData.nonConformance || ''}
                    onChange={handleNonConformanceChange}
                    placeholder="Enter details about non conformance..."
                    className="w-full border border-gray-300 p-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                    rows={4}
                />
            </div>

            <button
                onClick={submitObservations}
                className="mt-4 px-6 py-2 bg-[#424752] text-white rounded-lg hover:bg-gray-500 transition-colors duration-150"
            >
                Submit
            </button>
        </div>
    );
};

export default Curing;